/**
 * Vereinfachter Analytics Hook ohne Router-Abhängigkeit
 * Für Verwendung außerhalb des Router-Kontexts (z.B. in App.tsx)
 */

import { useEffect, useCallback, useRef } from 'react';
import analyticsService, { GAEvent } from '../services/analyticsService';
import logger from '../utils/logger';

interface UseAnalyticsSimpleReturn {
  // Status
  isEnabled: boolean;
  isInitialized: boolean;
  pendingEventCount: number;
  
  // Event-Tracking-Methoden
  trackEvent: (event: GAEvent) => void;
  
  // Consent Management
  enableAnalytics: () => void;
  disableAnalytics: () => void;
}

interface UseAnalyticsSimpleOptions {
  // Automatisches User Engagement Tracking
  trackUserEngagement?: boolean;
  // Debugging
  debug?: boolean;
}

/**
 * Vereinfachter Hook für Google Analytics ohne Router-Abhängigkeit
 */
export const useAnalyticsSimple = (options: UseAnalyticsSimpleOptions = {}): UseAnalyticsSimpleReturn => {
  const {
    trackUserEngagement = false,
    debug = false
  } = options;

  const engagementStartTime = useRef<number>(Date.now());
  const engagementTimer = useRef<NodeJS.Timeout | null>(null);

  // Event-Tracking-Methoden
  const trackEvent = useCallback((event: GAEvent) => {
    analyticsService.trackEvent(event);
    if (debug) {
      logger.debug('Analytics Event getrackt:', event);
    }
  }, [debug]);

  // Consent Management
  const enableAnalytics = useCallback(() => {
    analyticsService.enableAnalytics();
    if (debug) {
      logger.debug('Analytics aktiviert über einfachen Hook');
    }
  }, [debug]);

  const disableAnalytics = useCallback(() => {
    analyticsService.disableAnalytics();
    if (debug) {
      logger.debug('Analytics deaktiviert über einfachen Hook');
    }
  }, [debug]);

  // User Engagement Tracking (ohne Location-Abhängigkeit)
  useEffect(() => {
    if (!trackUserEngagement) return;

    engagementStartTime.current = Date.now();

    // Tracke Engagement nach 30 Sekunden
    engagementTimer.current = setTimeout(() => {
      const engagementTime = Date.now() - engagementStartTime.current;
      
      trackEvent({
        event_name: 'user_engagement',
        event_category: 'engagement',
        custom_parameters: {
          engagement_time_msec: engagementTime,
          page_path: window.location.pathname
        }
      });

      if (debug) {
        logger.debug(`User Engagement getrackt: ${Math.round(engagementTime / 1000)}s`);
      }
    }, 30000);

    return () => {
      if (engagementTimer.current) {
        clearTimeout(engagementTimer.current);
        engagementTimer.current = null;
      }
    };
  }, [trackUserEngagement, trackEvent, debug]);

  // Cleanup bei Unmount
  useEffect(() => {
    return () => {
      if (engagementTimer.current) {
        clearTimeout(engagementTimer.current);
      }
    };
  }, []);

  return {
    // Status
    isEnabled: analyticsService.enabled,
    isInitialized: analyticsService.initialized,
    pendingEventCount: analyticsService.pendingEventCount,
    
    // Event-Tracking
    trackEvent,
    
    // Consent Management
    enableAnalytics,
    disableAnalytics
  };
};

export default useAnalyticsSimple;


